import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import { adbApi, buildWsUrl } from '@/lib/api';
import {
  annexBtoAvcc,
  buildAvcCDescription,
  codecStringFromSps,
  parseSpsPpsFromAnnexB,
} from '@/lib/scrcpy/avc';
import {
  KEYCODE,
  KEY_ACTION,
  MOTION_ACTION,
  POINTER_ID_MOUSE,
  packInjectKeycode,
  packInjectScroll,
  packInjectText,
  packInjectTouch,
} from '@/lib/scrcpy/control';
import type { MirrorQuality } from '@/store/mirror';

const OP = {
  META: 0x01,
  CONFIG: 0x02,
  KEY: 0x03,
  DELTA: 0x04,
  SERVER_LOG: 0x05,
  DEVICE_MSG: 0x06,

  CONTROL: 0x10,
} as const;

/**
 * Scrcpy DEVICE_MSG type IDs (first byte of an OP.DEVICE_MSG payload).
 * Mirrors `DEVICE_MSG_TYPE` from `src/scrcpy/types.ts`. Only CLIPBOARD
 * is actually forwarded by the server today; the others are reserved
 * so the dispatch stays exhaustive if we ever expand the surface.
 */
const DEVICE_MSG = {
  CLIPBOARD: 0x00,
  ACK_CLIPBOARD: 0x01,
  UHID_OUTPUT: 0x02,
} as const;

export interface MirrorMeta {
  w: number;
  h: number;
  deviceName: string;
  codec: string;
  serverVersion: string;
  /**
   * Server `Date.now()` sampled in the same tick as `serverMonoUs`. Pair
   * with `serverMonoUs` to convert scrcpy PTS → wall-clock. Optional
   * because old servers (pre-HUD) didn't include the clock pair.
   */
  serverEpochMs?: number;
  /**
   * Server monotonic clock in microseconds (`process.hrtime.bigint() / 1000n`)
   * sampled in the same tick as `serverEpochMs`. Stringified bigint to
   * survive JSON.
   */
  serverMonoUs?: string;
}

/**
 * Rolling 1s sample for the HUD: FPS, current bandwidth, last frame e2e
 * latency. Recomputed on the canvas every ~1s; `null` when we don't have
 * enough data yet or when the server didn't ship a clock baseline.
 */
export interface MirrorPerf {
  fps: number;
  /** Average bandwidth over the last sample window, in kbit/s. */
  bitrateKbps: number;
  /**
   * End-to-end latency (server frame timestamp → browser draw), in ms.
   * `null` when the server's META did not include the clock baseline.
   */
  latencyMs: number | null;
}

export type MirrorStatus =
  | { kind: 'unsupported' }
  | { kind: 'connecting'; retry?: { count: number; max: number } }
  | { kind: 'live'; meta: MirrorMeta; perf: MirrorPerf | null }
  | { kind: 'error'; message: string; hint?: string };

const SUPPORTS_WEB_CODECS =
  typeof window !== 'undefined' && 'VideoDecoder' in window;

export interface MirrorCanvasHandle {
  /** Send a raw scrcpy control message (caller pre-encodes the body). */
  sendControl(bytes: Uint8Array): void;
  /** Reconnect with the current quality (use after a transient error). */
  reconnect(): void;
  /** Latest device dimensions, or null when not yet live. */
  getScreenSize(): { w: number; h: number } | null;
  /**
   * Start recording the canvas stream via `MediaRecorder`. Returns the
   * negotiated mime/extension on success, or `null` when the browser does
   * not support `MediaRecorder` + `canvas.captureStream` for any of the
   * candidate codecs.
   *
   * Why a canvas-side recorder rather than `adb shell screenrecord`?
   * Restrictive ROMs (Realme/OPPO/OnePlus on Android 13+) box the
   * `screenrecord` binary into a `screen_record` SELinux domain that
   * cannot `open()` files in shell-writable paths and cannot be killed by
   * the shell user — making device-side recording effectively impossible.
   * The browser already has the decoded frames; recording them is reliable
   * and identical to what the user sees.
   */
  startRecording(opts?: { fps?: number; bitsPerSecond?: number }): { mime: string; ext: string } | null;
  /**
   * Stop the in-progress recording and resolve with the resulting blob.
   * Returns `null` when no recording was active.
   */
  stopRecording(): Promise<{ blob: Blob; mime: string; ext: string } | null>;
  /** Whether a `MediaRecorder` session is currently capturing. */
  isRecording(): boolean;
}

export interface MirrorCanvasProps {
  serial: string;
  isOnline: boolean;
  quality: MirrorQuality;
  /**
   * Optional startup-time scrcpy overrides forwarded via WS query. Changing
   * any of these triggers a bridge restart — handled by listing them in the
   * effect's dependency array, identical to how `quality` already works.
   *
   *   - `videoBitRateKbps` (null → use server default per transport)
   *   - `stayAwake`        (true → scrcpy `stay_awake=true` + cleanup on)
   *   - `lockOrientation`  (-1 = unlocked, 0..3 = quarter rotations)
   */
  videoBitRateKbps?: number | null;
  stayAwake?: boolean;
  lockOrientation?: -1 | 0 | 1 | 2 | 3;
  /** Called whenever status transitions; lets the parent show toolbar state. */
  onStatusChange?: (status: MirrorStatus) => void;
  /** Called for every line emitted by the scrcpy server process on device. */
  onServerLog?: (level: number, line: string) => void;
  /**
   * Fired with the latest device clipboard text any time the server pushes
   * a CLIPBOARD device-message. The browser-side handler (toolbar) decides
   * whether to write into `navigator.clipboard` and surface a toast.
   */
  onDeviceClipboard?: (text: string) => void;
  /**
   * When true, the canvas only renders frames — no pointer / touch /
   * keyboard / wheel events are forwarded to the device. Use when a
   * device is publicly visible but the local user only has spectator
   * rights (e.g. someone else holds the active lease, Phase 3). The
   * scrcpy server is still started with INJECT enabled because the lease
   * holder may be on another tab/session; clamp at the UI layer is the
   * cheapest enforcement point.
   */
  readOnly?: boolean;
}

/**
 * Pure rendering + input shell for scrcpy mirroring. Owns the WebSocket and
 * the WebCodecs `VideoDecoder`; exposes `sendControl` for parents that need
 * to inject events from outside the canvas (e.g. toolbar buttons).
 *
 * The wheel handler is attached imperatively via `addEventListener` with
 * `{ passive: false }` because React 17+ registers `onWheel` as a passive
 * listener — `e.preventDefault()` then becomes a no-op and the outer page
 * scroll bleeds through, jolting the canvas. (This was the root cause of the
 * "上下滑动 手机页面会晃动" bug reported in the embedded tab.)
 */
export const MirrorCanvas = forwardRef<MirrorCanvasHandle, MirrorCanvasProps>(
  function MirrorCanvas(
    {
      serial,
      isOnline,
      quality,
      videoBitRateKbps = null,
      stayAwake = false,
      lockOrientation = -1,
      onStatusChange,
      onServerLog,
      onDeviceClipboard,
      readOnly = false,
    },
    ref,
  ) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const decoderRef = useRef<VideoDecoder | null>(null);
    const decoderConfiguredRef = useRef<boolean>(false);
    const lastFrameRef = useRef<VideoFrame | null>(null);

    const [status, setStatus] = useState<MirrorStatus>(() =>
      SUPPORTS_WEB_CODECS ? { kind: 'connecting' } : { kind: 'unsupported' }
    );
    const [reconnectNonce, setReconnectNonce] = useState(0);
    // Polled every 5s while the mirror is live; flips true when the
    // device's screen is off / asleep so we can overlay "📱 手机锁屏中"
    // on top of the (otherwise pitch-black) decoded frames. Without the
    // overlay the user sees a blank canvas and assumes the mirror is
    // broken. See `device.powerState` adb builtin for source data.
    const [screenLocked, setScreenLocked] = useState(false);
    // Latest 1s-window perf snapshot. Lifted out of `status` so the
    // sampler can update it independently of session lifecycle events.
    const [perf, setPerf] = useState<MirrorPerf | null>(null);

    const onStatusChangeRef = useRef(onStatusChange);
    onStatusChangeRef.current = onStatusChange;
    const onServerLogRef = useRef(onServerLog);
    onServerLogRef.current = onServerLog;
    const onDeviceClipboardRef = useRef(onDeviceClipboard);
    onDeviceClipboardRef.current = onDeviceClipboard;

    useEffect(() => {
      // Composite status: when live, attach the latest perf snapshot so the
      // toolbar HUD can update independently of session lifecycle events.
      const combined: MirrorStatus =
        status.kind === 'live' ? { ...status, perf } : status;
      onStatusChangeRef.current?.(combined);
    }, [status, perf]);

    const meta = status.kind === 'live' ? status.meta : null;
    const screenW = meta?.w ?? 0;
    const screenH = meta?.h ?? 0;

    /* ---------------- WebSocket + decoder bootstrap ---------------- */
    /*
     * Connection lifecycle:
     *
     *  - One session = one WebSocket + one VideoDecoder.
     *  - Effect deps: serial / isOnline / quality / reconnectNonce. When
     *    quality changes, the old session is torn down and a new one is
     *    started — but we delay the new connect by ~150ms so the server has
     *    time to release the old `ScrcpyBridge` and the new `acquire()` will
     *    spawn a fresh bridge (avoiding the race where the new client picks
     *    up the old bridge with the old maxSize).
     *  - WS close / `error` / decoder fail → exponential-backoff retry up
     *    to MAX_RETRIES; only then do we surface an error to the user. This
     *    prevents transient "A key frame is required" / "ws closed cleanly"
     *    blips during quality switches from showing as a hard error.
     *  - DELTA frames arriving before the first KEY after configure are
     *    silently dropped. WebCodecs requires a keyframe right after
     *    configure(); replaying a stale DELTA from the bridge cache (or a
     *    pre-keyframe DELTA from the wire) would otherwise throw.
     *  - Permission errors (INJECT_EVENTS missing) and config-parse failures
     *    are FATAL — retrying won't help, we surface immediately.
     */
    /**
     * `clockOffsetMs`: server-PTS basis → browser wall-clock translation.
     * Set on every META; `null` until META lands or when the server
     * didn't include the clock pair. Computed as
     * `serverEpochMs - serverMonoUs/1000` so that for any frame PTS the
     * wall-clock instant the frame represents is `offset + pts/1000`.
     */
    const clockOffsetMsRef = useRef<number | null>(null);
    /** Last frame e2e latency in ms — captured in `drawFrame`. */
    const lastLatencyMsRef = useRef<number | null>(null);
    /** Rolling counters for the perf sampler (1s window). */
    const framesInWindowRef = useRef(0);
    const bytesInWindowRef = useRef(0);
    const lastSampleAtRef = useRef(performance.now());

    useEffect(() => {
      if (!SUPPORTS_WEB_CODECS) return;
      if (!isOnline || !serial) return;

      // Why 7 retries with a 2 s cap: the server reconcile loop runs on
      // a 5 s tick and flips `activeTransport` from USB → Wi-Fi only on
      // the next tick after the cable is unplugged. With 4 retries we
      // had ~3.75 s of total budget and gave up before the loop fired,
      // showing a spurious "镜像连接失败" right when the device was
      // about to come back on Wi-Fi. 7 retries gives 0.25 + 0.5 + 1 +
      // 2*5 ≈ 11.75 s — enough to span two reconcile ticks AND the
      // adb-server spawn delay for the fresh wireless `scrcpy` bridge.
      const MAX_RETRIES = 7;
      const backoffMs = (n: number): number => Math.min(2000, 250 * 2 ** n);

      let cancelled = false;
      let retryCount = 0;
      let connectTimer: ReturnType<typeof setTimeout> | null = null;
      let activeWs: WebSocket | null = null;
      let activeDecoder: VideoDecoder | null = null;

      const drawFrame = (frame: VideoFrame): void => {
        const canvas = canvasRef.current;
        if (!canvas) {
          frame.close();
          return;
        }
        if (canvas.width !== frame.codedWidth || canvas.height !== frame.codedHeight) {
          canvas.width = frame.codedWidth;
          canvas.height = frame.codedHeight;
        }
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.drawImage(frame, 0, 0);
        // HUD latency sample. `frame.timestamp` is sourced from the
        // EncodedVideoChunk's timestamp we set when calling decode(), which
        // is the server-side PTS in microseconds. Combined with the
        // META-derived offset, that gives us a wall-clock estimate of when
        // the server captured the frame — comparing with `Date.now()`
        // yields end-to-end latency.
        const offset = clockOffsetMsRef.current;
        if (offset != null) {
          lastLatencyMsRef.current = Date.now() - (offset + frame.timestamp / 1000);
        }
        framesInWindowRef.current += 1;
        lastFrameRef.current?.close();
        lastFrameRef.current = frame;
      };

      const teardownActive = (): void => {
        if (activeWs) {
          try {
            activeWs.close();
          } catch {
            /* ignore */
          }
          activeWs = null;
        }
        if (activeDecoder) {
          try {
            if (activeDecoder.state !== 'closed') activeDecoder.close();
          } catch {
            /* ignore */
          }
          activeDecoder = null;
        }
        wsRef.current = null;
        decoderRef.current = null;
        decoderConfiguredRef.current = false;
      };

      /** Schedule a retry; if budget exhausted, surface an error. */
      const scheduleRetry = (lastHint: string): void => {
        if (cancelled) return;
        teardownActive();
        if (retryCount >= MAX_RETRIES) {
          setStatus({
            kind: 'error',
            message: '镜像连接失败',
            hint: `已重试 ${MAX_RETRIES} 次：${lastHint || '无法建立连接'}`,
          });
          return;
        }
        retryCount += 1;
        setStatus({ kind: 'connecting', retry: { count: retryCount, max: MAX_RETRIES } });
        const delay = backoffMs(retryCount - 1);
        connectTimer = setTimeout(() => {
          connectTimer = null;
          startSession();
        }, delay);
      };

      /** Open a new WebSocket + decoder. */
      const startSession = (): void => {
        if (cancelled) return;
        teardownActive();

        // Treat -1 / 0 as "let server decide" — the per-transport profile
        // in MirrorManager.acquire then picks 900p/30fps for wireless and
        // 1080p/60fps for USB instead of blindly forcing 1080p/60fps and
        // choking the wireless link. Only forward positive ints (an
        // explicit user pick from the toolbar dropdown).
        const url = buildWsUrl('/api/screen/stream', {
          serial,
          maxSize: quality.maxSize > 0 ? quality.maxSize : undefined,
          maxFps: quality.maxFps > 0 ? quality.maxFps : undefined,
          // Phase 3: pipe through startup-time scrcpy overrides. `kbps * 1000`
          // because scrcpy's `video_bit_rate=` is in bits/s, while our store
          // (and the toolbar UI) keep things in kbps for ergonomic numbers.
          // Sentinels (`null`, `false`, `-1`) collapse to `undefined` so the
          // URL stays clean and the server falls through to its per-transport
          // default exactly as before this phase shipped.
          bitRate:
            videoBitRateKbps != null && videoBitRateKbps > 0
              ? videoBitRateKbps * 1000
              : undefined,
          stayAwake: stayAwake ? 1 : undefined,
          lockOrientation: lockOrientation >= 0 ? lockOrientation : undefined,
        });
        const ws = new WebSocket(url);
        ws.binaryType = 'arraybuffer';
        activeWs = ws;
        wsRef.current = ws;

        // Per-session flag — must be reset every time we (re)configure.
        let needsKeyframe = true;
        let metaPayload: MirrorMeta | null = null;
        let sessionConfigured = false;
        let sawFirstFrame = false;

        const decoder = new VideoDecoder({
          output: (frame) => {
            if (!sawFirstFrame) {
              sawFirstFrame = true;
              // First successful frame → connection healthy, reset budget.
              retryCount = 0;
            }
            drawFrame(frame);
          },
          error: (e) => {
            if (cancelled || activeDecoder !== decoder) return;
            const msg = e instanceof Error ? e.message : String(e);
            scheduleRetry(`解码失败：${msg}`);
          },
        });
        activeDecoder = decoder;
        decoderRef.current = decoder;

        ws.addEventListener('message', (ev) => {
          const data = ev.data as ArrayBuffer;
          if (!(data instanceof ArrayBuffer) || data.byteLength < 1) return;
          if (cancelled || activeWs !== ws) return;
          const view = new Uint8Array(data);
          const op = view[0];
          const payload = view.subarray(1);

          if (op === OP.META) {
            try {
              const json = new TextDecoder().decode(payload);
              metaPayload = JSON.parse(json) as MirrorMeta;
              // Build the PTS→wall-clock offset right when META lands so
              // the very first KEY/DELTA can already report latency.
              if (metaPayload.serverEpochMs && metaPayload.serverMonoUs) {
                const monoUs = Number(metaPayload.serverMonoUs);
                if (Number.isFinite(monoUs)) {
                  clockOffsetMsRef.current = metaPayload.serverEpochMs - monoUs / 1000;
                }
              } else {
                clockOffsetMsRef.current = null;
              }
            } catch {
              /* ignore */
            }
          } else if (op === OP.CONFIG) {
            const parsed = parseSpsPpsFromAnnexB(payload);
            if (!parsed) {
              setStatus({ kind: 'error', message: '协议错误', hint: '无法解析 SPS/PPS' });
              cancelled = true;
              teardownActive();
              return;
            }
            const description = buildAvcCDescription(parsed);
            const codec = codecStringFromSps(parsed);
            const config: VideoDecoderConfig = {
              codec,
              codedWidth: metaPayload?.w,
              codedHeight: metaPayload?.h,
              description,
              optimizeForLatency: true,
              hardwareAcceleration: 'prefer-hardware',
            };
            try {
              if (sessionConfigured) decoder.reset();
              decoder.configure(config);
              sessionConfigured = true;
              decoderConfiguredRef.current = true;
              // After (re)configure the very next chunk MUST be a keyframe.
              needsKeyframe = true;
            } catch (e) {
              scheduleRetry(`配置解码器失败：${e instanceof Error ? e.message : String(e)}`);
              return;
            }
            if (metaPayload) setStatus({ kind: 'live', meta: metaPayload, perf: null });
          } else if (op === OP.KEY || op === OP.DELTA) {
            if (!sessionConfigured) return;
            if (payload.byteLength < 8) return;
            // Drop deltas until we see a keyframe (post-configure or after a
            // missed keyframe). This eliminates "A key frame is required" errors.
            if (op === OP.DELTA && needsKeyframe) return;
            const ptsHi = new DataView(payload.buffer, payload.byteOffset, 8).getUint32(0, false);
            const ptsLo = new DataView(payload.buffer, payload.byteOffset, 8).getUint32(4, false);
            const pts = BigInt(ptsHi) * 0x1_0000_0000n + BigInt(ptsLo);
            const annexB = payload.subarray(8);
            const avcc = annexBtoAvcc(annexB);
            // Track network bytes for the bandwidth HUD. The 9-byte
            // header is included to reflect actual wire size.
            bytesInWindowRef.current += data.byteLength;
            try {
              decoder.decode(
                new EncodedVideoChunk({
                  type: op === OP.KEY ? 'key' : 'delta',
                  timestamp: Number(pts),
                  data: avcc,
                })
              );
              if (op === OP.KEY) needsKeyframe = false;
            } catch (e) {
              const msg = e instanceof Error ? e.message : String(e);
              // "A key frame is required" → drop and wait for the next KEY.
              if (/key frame is required/i.test(msg)) {
                needsKeyframe = true;
                return;
              }
              scheduleRetry(`解码 chunk 失败：${msg}`);
            }
          } else if (op === OP.SERVER_LOG) {
            if (payload.byteLength < 1) return;
            const lvl = payload[0];
            const line = new TextDecoder().decode(payload.subarray(1));
            onServerLogRef.current?.(lvl, line);
            if (lvl >= 1 && /INJECT_EVENTS permission/i.test(line)) {
              // Permission failure is fatal — retrying won't help.
              setStatus({
                kind: 'error',
                message: 'MIUI/HyperOS 缺少 INJECT_EVENT 权限',
                hint: '请进入 设置 → 开发者选项 → 启用 “USB 调试 (安全设置)”，然后重连设备。',
              });
              cancelled = true;
              teardownActive();
            }
          } else if (op === OP.DEVICE_MSG) {
            // Wire format: u8 device_msg_type | <type-specific body>
            if (payload.byteLength < 1) return;
            const msgType = payload[0];
            if (msgType === DEVICE_MSG.CLIPBOARD) {
              // CLIPBOARD body: u32 BE textLen | <textLen UTF-8>
              if (payload.byteLength < 5) return;
              const textLen = new DataView(
                payload.buffer,
                payload.byteOffset + 1,
                4,
              ).getUint32(0, false);
              if (payload.byteLength < 5 + textLen) return;
              const text = new TextDecoder('utf-8').decode(
                payload.subarray(5, 5 + textLen),
              );
              onDeviceClipboardRef.current?.(text);
            }
            // ACK_CLIPBOARD / UHID_OUTPUT are intentionally dropped —
            // nothing in the UI consumes them today.
          }
        });

        ws.addEventListener('close', (ev) => {
          if (cancelled || activeWs !== ws) return;
          // Code 1000 with no prior live status means server rejected → retry.
          // Otherwise a clean live close is also retried (e.g. mid-stream).
          scheduleRetry(ev.reason || `ws closed (code ${ev.code})`);
        });
        ws.addEventListener('error', () => {
          // Browsers always fire `close` after `error`; let close handle the retry
          // so we don't double-schedule.
        });
      };

      // Initial connect: tiny delay so an immediate quality-switch lets the
      // server fully release the previous bridge before we acquire again.
      setStatus({ kind: 'connecting' });
      connectTimer = setTimeout(() => {
        connectTimer = null;
        startSession();
      }, 150);

      return () => {
        cancelled = true;
        if (connectTimer != null) {
          clearTimeout(connectTimer);
          connectTimer = null;
        }
        teardownActive();
        lastFrameRef.current?.close();
        lastFrameRef.current = null;
      };
    }, [
      serial,
      isOnline,
      quality.maxSize,
      quality.maxFps,
      videoBitRateKbps,
      stayAwake,
      lockOrientation,
      reconnectNonce,
    ]);

    /* ---------------- HUD perf sampler (1s window) ---------------- */
    /*
     * Sample every 1s while the mirror is live. Reset window counters
     * after each tick so we always report rate-over-the-last-second, not
     * rate-since-session-start (the latter would slowly converge to a
     * useless static value).
     *
     * `latencyMs` is the *last* frame's end-to-end latency rather than an
     * average — for a HUD users want to see spikes as they happen.
     */
    const liveForPerf = status.kind === 'live';
    useEffect(() => {
      if (!liveForPerf) {
        setPerf(null);
        framesInWindowRef.current = 0;
        bytesInWindowRef.current = 0;
        lastLatencyMsRef.current = null;
        return;
      }
      lastSampleAtRef.current = performance.now();
      const id = window.setInterval(() => {
        const now = performance.now();
        const dtMs = now - lastSampleAtRef.current;
        lastSampleAtRef.current = now;
        const dtSec = dtMs / 1000;
        const frames = framesInWindowRef.current;
        const bytes = bytesInWindowRef.current;
        framesInWindowRef.current = 0;
        bytesInWindowRef.current = 0;
        if (dtSec <= 0) return;
        setPerf({
          fps: Math.round(frames / dtSec),
          bitrateKbps: Math.round((bytes * 8) / dtMs), // bytes*8/ms = kbit/s
          latencyMs: lastLatencyMsRef.current,
        });
      }, 1000);
      return () => window.clearInterval(id);
    }, [liveForPerf]);

    /* ---------------- Screen-locked polling ---------------- */
    /*
     * While the mirror is live, ping `device.powerState` every 5s. When the
     * device is no longer interactive (Asleep / Dozing / screen-off), flip
     * `screenLocked` so we can paint an overlay on top of the (otherwise
     * black) canvas — telling the user the mirror is fine, the phone just
     * went to sleep.
     *
     * Why poll vs. parse a scrcpy event: scrcpy doesn't surface a power
     * event on the wire; this is the cheapest signal we have. 5s is the
     * sweet spot between "feels live" and "doesn't hammer adb". We pause
     * polling whenever the mirror isn't live (connecting / error) — the
     * canvas already has a foreground state for those.
     */
    const live = status.kind === 'live';
    useEffect(() => {
      if (!serial || !isOnline || !live) {
        setScreenLocked(false);
        return undefined;
      }
      let cancelled = false;
      const probe = async (): Promise<void> => {
        try {
          const { result } = await adbApi.run<{
            interactive: boolean;
            screenOn: boolean;
          }>({ serial, name: 'device.powerState' });
          if (!cancelled) {
            // `interactive: false` covers Asleep / Dozing / Dreaming — all the
            // states where input injection is no-op'd. `screenOn: false` is a
            // narrower "panel literally dark" signal we treat as the same.
            setScreenLocked(!result.interactive || !result.screenOn);
          }
        } catch {
          // Don't flip the overlay on transient adb failures — false positives
          // here would make the canvas look broken for no reason.
        }
      };
      void probe();
      const id = window.setInterval(() => void probe(), 5000);
      return () => {
        cancelled = true;
        clearInterval(id);
      };
    }, [serial, isOnline, live]);

    /* ---------------- Send-control helper ---------------- */
    const sendControl = useMemo(
      () =>
        (bytes: Uint8Array): void => {
          const ws = wsRef.current;
          if (!ws || ws.readyState !== WebSocket.OPEN) return;
          const frame = new Uint8Array(bytes.length + 1);
          frame[0] = OP.CONTROL;
          frame.set(bytes, 1);
          ws.send(frame);
        },
      []
    );

    /* Imperative API for the surrounding floating frame & toolbar */
    /* ---------------- MediaRecorder (canvas → blob) ---------------- */
    const recorderRef = useRef<MediaRecorder | null>(null);
    const recorderChunksRef = useRef<Blob[]>([]);
    const recorderInfoRef = useRef<{ mime: string; ext: string } | null>(null);

    const startRecording = (opts?: { fps?: number; bitsPerSecond?: number }) => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      if (recorderRef.current) return recorderInfoRef.current;
      if (typeof MediaRecorder === 'undefined') return null;
      // Try formats most likely to play directly on user's machine first.
      // Safari prefers mp4; Chrome/Edge typically yield WebM.
      const candidates: { mime: string; ext: string }[] = [
        { mime: 'video/mp4; codecs="avc1.42E01E"', ext: 'mp4' },
        { mime: 'video/webm; codecs="vp9,opus"', ext: 'webm' },
        { mime: 'video/webm; codecs=vp9', ext: 'webm' },
        { mime: 'video/webm; codecs=vp8', ext: 'webm' },
        { mime: 'video/webm', ext: 'webm' },
      ];
      const picked = candidates.find((c) => MediaRecorder.isTypeSupported(c.mime));
      if (!picked) return null;
      let stream: MediaStream;
      try {
        stream = (canvas as HTMLCanvasElement & {
          captureStream(frameRate?: number): MediaStream;
        }).captureStream(opts?.fps ?? 30);
      } catch {
        return null;
      }
      let rec: MediaRecorder;
      try {
        rec = new MediaRecorder(stream, {
          mimeType: picked.mime,
          videoBitsPerSecond: opts?.bitsPerSecond ?? 8_000_000,
        });
      } catch {
        return null;
      }
      recorderChunksRef.current = [];
      rec.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) recorderChunksRef.current.push(ev.data);
      };
      try {
        rec.start(1000); // emit a chunk every 1s so progress is preserved if tab dies
      } catch {
        return null;
      }
      recorderRef.current = rec;
      recorderInfoRef.current = picked;
      return picked;
    };

    const stopRecording = (): Promise<{ blob: Blob; mime: string; ext: string } | null> => {
      const rec = recorderRef.current;
      const info = recorderInfoRef.current;
      if (!rec || !info) return Promise.resolve(null);
      return new Promise((resolve) => {
        rec.onstop = () => {
          const blob = new Blob(recorderChunksRef.current, { type: info.mime });
          recorderChunksRef.current = [];
          recorderRef.current = null;
          recorderInfoRef.current = null;
          // Stop the underlying tracks so the canvas isn't held open.
          for (const track of rec.stream.getTracks()) {
            try {
              track.stop();
            } catch {
              /* ignore */
            }
          }
          resolve({ blob, mime: info.mime, ext: info.ext });
        };
        try {
          if (rec.state !== 'inactive') rec.stop();
          else resolve(null);
        } catch {
          resolve(null);
        }
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        sendControl,
        reconnect: () => setReconnectNonce((n) => n + 1),
        getScreenSize: () =>
          status.kind === 'live' ? { w: status.meta.w, h: status.meta.h } : null,
        startRecording,
        stopRecording,
        isRecording: () => recorderRef.current?.state === 'recording',
      }),
      [sendControl, status]
    );

    /* ---------------- Pointer / touch / key handlers ---------------- */
    /*
     * Canvas uses `object-contain` so the decoded video keeps its aspect
     * ratio inside a possibly-mismatched container. That means
     * `getBoundingClientRect()` returns the FULL canvas box including
     * any letterbox gutters (vertical bars when the container is wider
     * than the device aspect, horizontal bars when it's taller). A
     * naive `(cssX / rect.width) * screenW` then bakes the gutter into
     * the device coordinate — symptom: tapping the rightmost icon in
     * the launcher launches the icon to its LEFT because the right
     * gutter steals a chunk of the X mapping. The fix: compute the
     * actual displayed content rect from min(scaleX, scaleY) and
     * center-justify it inside the canvas box before scaling.
     */
    const canvasToDevice = (
      clientX: number,
      clientY: number
    ): { x: number; y: number } | null => {
      const canvas = canvasRef.current;
      if (!canvas || !screenW || !screenH) return null;
      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return null;
      const scale = Math.min(rect.width / screenW, rect.height / screenH);
      const contentW = screenW * scale;
      const contentH = screenH * scale;
      // Letterbox offsets — the content sits dead-centre under
      // `object-contain`.
      const offsetX = (rect.width - contentW) / 2;
      const offsetY = (rect.height - contentH) / 2;
      const cssX = clientX - rect.left - offsetX;
      const cssY = clientY - rect.top - offsetY;
      // Clicks inside the gutter are silently snapped to the content
      // edge (clamped below). We could ignore them, but snapping feels
      // closer to native behaviour for fat-finger taps right at the
      // bezel.
      const x = Math.round((cssX / contentW) * screenW);
      const y = Math.round((cssY / contentH) * screenH);
      return {
        x: Math.max(0, Math.min(screenW - 1, x)),
        y: Math.max(0, Math.min(screenH - 1, y)),
      };
    };

    const sendTouch = (
      action: number,
      pointerId: bigint,
      clientX: number,
      clientY: number,
      pressure: number,
      button = 0
    ): void => {
      const xy = canvasToDevice(clientX, clientY);
      if (!xy) return;
      sendControl(
        packInjectTouch({
          action,
          pointerId,
          x: xy.x,
          y: xy.y,
          screenWidth: screenW,
          screenHeight: screenH,
          pressure,
          actionButton: button,
          buttons: pressure > 0 ? button : 0,
        })
      );
    };

    /* mouse */
    const draggingRef = useRef(false);
    const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>): void => {
      if (status.kind !== 'live') return;
      if (e.pointerType === 'touch') return;
      e.preventDefault();
      canvasRef.current?.setPointerCapture(e.pointerId);
      draggingRef.current = true;
      sendTouch(MOTION_ACTION.DOWN, POINTER_ID_MOUSE, e.clientX, e.clientY, 1, 1);
    };
    const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>): void => {
      if (status.kind !== 'live' || !draggingRef.current) return;
      if (e.pointerType === 'touch') return;
      sendTouch(MOTION_ACTION.MOVE, POINTER_ID_MOUSE, e.clientX, e.clientY, 1, 1);
    };
    const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>): void => {
      if (status.kind !== 'live') return;
      if (e.pointerType === 'touch') return;
      if (!draggingRef.current) return;
      draggingRef.current = false;
      sendTouch(MOTION_ACTION.UP, POINTER_ID_MOUSE, e.clientX, e.clientY, 0, 1);
    };

    /* touch (multi-finger) */
    const onTouchEvent = (
      e: React.TouchEvent<HTMLCanvasElement>,
      phase: 'start' | 'move' | 'end' | 'cancel'
    ): void => {
      if (status.kind !== 'live') return;
      e.preventDefault();
      const touches = phase === 'end' || phase === 'cancel' ? e.changedTouches : e.touches;
      for (let i = 0; i < touches.length; i++) {
        const t = touches[i];
        const action =
          phase === 'start'
            ? MOTION_ACTION.DOWN
            : phase === 'move'
            ? MOTION_ACTION.MOVE
            : phase === 'end'
            ? MOTION_ACTION.UP
            : MOTION_ACTION.CANCEL;
        const pressure = phase === 'end' || phase === 'cancel' ? 0 : 1;
        sendTouch(action, BigInt(t.identifier & 0xffff), t.clientX, t.clientY, pressure, 0);
      }
    };

    /**
     * Wheel listener registered imperatively with `{ passive: false }` so
     * `preventDefault()` actually blocks the outer scroll. React's synthetic
     * `onWheel` would be passive and the page would jank.
     */
    useEffect(() => {
      if (readOnly) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const handler = (e: WheelEvent): void => {
        if (status.kind !== 'live') return;
        e.preventDefault();
        const xy = canvasToDevice(e.clientX, e.clientY);
        if (!xy) return;
        const norm = (n: number): number => Math.max(-16, Math.min(16, -n / 50));
        sendControl(
          packInjectScroll({
            x: xy.x,
            y: xy.y,
            screenWidth: screenW,
            screenHeight: screenH,
            hscroll: norm(e.deltaX),
            vscroll: norm(e.deltaY),
            buttons: 0,
          })
        );
      };
      canvas.addEventListener('wheel', handler, { passive: false });
      return () => canvas.removeEventListener('wheel', handler);
    }, [status, screenW, screenH, sendControl, readOnly]);

    const onKeyDown = (e: React.KeyboardEvent<HTMLCanvasElement>): void => {
      if (status.kind !== 'live') return;
      const mapped = mapKey(e.key);
      if (mapped !== null) {
        e.preventDefault();
        sendControl(
          packInjectKeycode({ action: KEY_ACTION.DOWN, keycode: mapped, repeat: 0, metaState: 0 })
        );
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        sendControl(packInjectText(e.key));
      }
    };
    const onKeyUp = (e: React.KeyboardEvent<HTMLCanvasElement>): void => {
      if (status.kind !== 'live') return;
      const mapped = mapKey(e.key);
      if (mapped !== null) {
        e.preventDefault();
        sendControl(
          packInjectKeycode({ action: KEY_ACTION.UP, keycode: mapped, repeat: 0, metaState: 0 })
        );
      }
    };

    /* ---------------- Render ---------------- */
    if (status.kind === 'unsupported') {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 bg-black/80 px-6 text-center text-amber-100">
          <AlertTriangle className="h-6 w-6" />
          <div className="text-sm font-semibold">浏览器不支持 WebCodecs</div>
          <div className="text-xs opacity-90">
            请使用 Chrome 94+ / Edge / Safari 17+ 打开本页面。
          </div>
        </div>
      );
    }

    if (!isOnline) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 bg-black/80 px-6 text-center text-amber-100">
          <AlertTriangle className="h-6 w-6" />
          <div className="text-sm font-semibold">设备已离线</div>
          <div className="text-xs opacity-90">请连接设备并授权 USB 调试后再开启镜像。</div>
        </div>
      );
    }

    return (
      <div className="relative h-full w-full bg-black">
        <canvas
          ref={canvasRef}
          tabIndex={readOnly ? -1 : 0}
          className={clsx('h-full w-full touch-none select-none object-contain outline-none', readOnly && 'cursor-not-allowed')}
          onPointerDown={readOnly ? undefined : onPointerDown}
          onPointerMove={readOnly ? undefined : onPointerMove}
          onPointerUp={readOnly ? undefined : onPointerUp}
          onPointerCancel={readOnly ? undefined : onPointerUp}
          onTouchStart={readOnly ? undefined : (e) => onTouchEvent(e, 'start')}
          onTouchMove={readOnly ? undefined : (e) => onTouchEvent(e, 'move')}
          onTouchEnd={readOnly ? undefined : (e) => onTouchEvent(e, 'end')}
          onTouchCancel={readOnly ? undefined : (e) => onTouchEvent(e, 'cancel')}
          onKeyDown={readOnly ? undefined : onKeyDown}
          onKeyUp={readOnly ? undefined : onKeyUp}
          onContextMenu={(e) => e.preventDefault()}
          title={readOnly ? '只读镜像 — 你当前没有控制权（spectator）' : undefined}
        />
        {readOnly && (
          <div className="pointer-events-none absolute right-2 top-2 rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-200">
            只读 · spectator
          </div>
        )}
        {/* Phone locked / screen off — overlay on top of the live canvas so
            users don't mistake the (literally black) frames for a broken
            mirror. Stays under the `connecting` / `error` overlays because
            those are about the mirror itself; screen-off is a device-side
            state we still want to surface clearly. */}
        {live && screenLocked && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 text-slate-100">
            <div className="text-2xl">🌙</div>
            <div className="text-sm font-medium">手机锁屏中</div>
            <div className="text-[11px] text-slate-300">
              按电源键或在工具栏点亮屏幕即可继续操作
            </div>
          </div>
        )}
        {status.kind === 'connecting' && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-ink-muted">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="ml-2 text-sm">
              {status.retry
                ? `正在重新连接手机画面… (${status.retry.count}/${status.retry.max})`
                : '正在连接手机画面…'}
            </span>
          </div>
        )}
        {status.kind === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/85 px-6 text-center text-rose-200">
            <AlertTriangle className="h-6 w-6" />
            <div className="text-sm font-semibold">{status.message}</div>
            {status.hint && <div className="text-xs text-rose-200/80">{status.hint}</div>}
            <button
              className="btn mt-2 text-xs"
              onClick={() => setReconnectNonce((n) => n + 1)}
            >
              重连
            </button>
          </div>
        )}
      </div>
    );
  }
);

/** Map a W3C `KeyboardEvent.key` to a scrcpy KEYCODE_* (returns null for printable text). */
function mapKey(key: string): number | null {
  switch (key) {
    case 'Backspace':
      return KEYCODE.DEL;
    case 'Enter':
      return KEYCODE.ENTER;
    case 'Tab':
      return KEYCODE.TAB;
    case 'Escape':
      return KEYCODE.ESCAPE;
    case 'ArrowUp':
      return KEYCODE.DPAD_UP;
    case 'ArrowDown':
      return KEYCODE.DPAD_DOWN;
    case 'ArrowLeft':
      return KEYCODE.DPAD_LEFT;
    case 'ArrowRight':
      return KEYCODE.DPAD_RIGHT;
    case 'Home':
      return KEYCODE.MOVE_HOME;
    case 'End':
      return KEYCODE.MOVE_END;
    case 'Delete':
      return KEYCODE.FORWARD_DEL;
    default:
      return null;
  }
}
